import { getCrdtAlgorithmFromConfigOrDefault } from '../crdts/CrdtAlgorithmRegistry.js';
import { getRecipe, resolveRuleInheritance } from '../object-recipes.js';
import { sparseMap } from './array.js';
import { isObject } from './type-checks-basic.js';
export function isIterationStrategy(arg) {
    return arg === 'off' || arg === 'parallel' || arg === 'separate';
}
export function isMapIterationStrategy(arg) {
    return (isObject(arg) &&
        isIterationStrategy(arg.valueIterationStrategy) &&
        isIterationStrategy(arg.keyIterationStrategy));
}
/**
 * Iterate over multiple objects at the same time and calling the passed callbacks.
 *
 * @param {T[]} objs
 * @param {Callbacks} cb
 * @param {IterateOptions} options
 */
export function iterateObjectsSync(objs, cb, options) {
    iterateAnyObjectsSync(objs, cb, options || {}, false);
}
/**
 * Iterate over a single id-object and call the specified callbacks
 *
 * @param {T} objs
 * @param {Callbacks} cb
 * @param {IterateOptions} options
 */
export function iterateIdObjectsSync(objs, cb, options) {
    iterateAnyObjectsSync(objs, cb, options || {}, true);
}
/**
 * Iterate over a single (id-)object and call the specified callbacks
 *
 * @param {T} objs
 * @param {Callbacks} cb
 * @param {IterateOptions} options
 * @param {boolean} idObject
 */
export function iterateAnyObjectsSync(objs, cb, options, idObject) {
    const types = new Set();
    for (const obj of objs) {
        if (obj !== undefined) {
            types.add(obj.$type$);
        }
    }
    if (types.size !== 1) {
        throw new Error('All objects passed to iterateObjects need to be of same type');
    }
    const recipe = getRecipe([...types][0]);
    const params = {
        crdtConfig: recipe.crdtConfig || new Map(),
        defaultIterationStrategies: {
            mapKeys: options.defaultIterationStrategies?.mapKeys || 'off',
            mapValues: options.defaultIterationStrategies?.mapValues || 'parallel',
            arrayValues: options.defaultIterationStrategies?.arrayValues || 'separate',
            bagValues: options.defaultIterationStrategies?.bagValues || 'separate',
            setValues: options.defaultIterationStrategies?.setValues || 'separate',
            objectValues: options.defaultIterationStrategies?.objectValues || 'parallel'
        },
        continueOnReadErrors: options.continueOnReadErrors === undefined ? false : options.continueOnReadErrors,
        onValueChange: options.onValueChange === undefined ? () => { } : options.onValueChange
    };
    iterateObjectValue({ type: 'object', rules: recipe.rule }, objs, '', cb, params, (i, value) => {
        objs[i] = value;
        params.onValueChange('', i, value);
    }, idObject);
}
// ######## Iterate various ValueTypes ########
function iterateRecipeValue(recipeValue, values, path, cb, p, setValue) {
    if (values.length === 0) {
        return;
    }
    const cbArgs = {
        values: values,
        valueType: recipeValue,
        path,
        crdtAlgorithm: getCrdtAlgorithmFromConfigOrDefault(p.crdtConfig, path, recipeValue.type),
        setValue
    };
    switch (recipeValue.type) {
        case 'string':
            cb.string && cb.string(cbArgs);
            break;
        case 'integer':
            cb.integer && cb.integer(cbArgs);
            break;
        case 'number':
            cb.number && cb.number(cbArgs);
            break;
        case 'boolean':
            cb.boolean && cb.boolean(cbArgs);
            break;
        case 'referenceToObj':
            iterateReferenceToObjectValue(recipeValue, values, path, cb, p, setValue);
            break;
        case 'referenceToId':
            iterateReferenceToIdValue(recipeValue, values, path, cb, p, setValue);
            break;
        case 'referenceToClob':
            cb.referenceToClob && cb.referenceToClob(cbArgs);
            break;
        case 'referenceToBlob':
            cb.referenceToBlob && cb.referenceToBlob(cbArgs);
            break;
        case 'map':
            iterateMapValue(recipeValue, values, path, cb, p, setValue);
            break;
        case 'bag':
            iterateBagValue(recipeValue, values, path, cb, p, setValue);
            break;
        case 'array':
            iterateArrayValue(recipeValue, values, path, cb, p, setValue);
            break;
        case 'set':
            iterateSetValue(recipeValue, values, path, cb, p, setValue);
            break;
        case 'object':
            iterateObjectValue(recipeValue, values, path, cb, p, setValue, false);
            break;
        case 'stringifiable':
            cb.stringifiable && cb.stringifiable(cbArgs);
            break;
    }
}
function iterateReferenceToObjectValue(recipeValue, hashes, path, cb, params, setValue) {
    cb.referenceToObj &&
        cb.referenceToObj({
            values: hashes,
            valueType: recipeValue,
            path,
            crdtAlgorithm: getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, path, 'referenceToObj'),
            setValue
        });
}
function iterateReferenceToIdValue(recipeValue, hashes, path, cb, params, setValue) {
    cb.referenceToId &&
        cb.referenceToId({
            values: hashes,
            valueType: recipeValue,
            path,
            crdtAlgorithm: getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, path, 'referenceToId'),
            setValue
        });
}
function iterateObjectValue(recipeValue, objs, path, cb, params, setValue, idObject) {
    cb.object &&
        cb.object({
            values: objs,
            valueType: recipeValue,
            path,
            crdtAlgorithm: getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, path, 'object'),
            setValue
        });
    for (const rawRule of recipeValue.rules) {
        const rule = resolveRuleInheritance(rawRule);
        const values = sparseMap(objs, obj => obj[rule.itemprop]);
        const propPath = path.length === 0 ? rule.itemprop : path.concat('.', rule.itemprop);
        const itemtype = rule.itemtype || { type: 'string' };
        if (idObject && rule.isId !== true) {
            continue;
        }
        const iterate = cb.objectProperty &&
            cb.objectProperty({
                values: values,
                valueType: itemtype,
                path: path.length === 0 ? rule.itemprop : path.concat('.', rule.itemprop),
                crdtAlgorithm: getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, propPath, 'objectProperty'),
                rule: rule,
                setValue: (i, value) => {
                    if (value === undefined) {
                        delete objs[i][rule.itemprop];
                    }
                    else {
                        objs[i][rule.itemprop] = value;
                    }
                    params.onValueChange(propPath, i, value);
                },
                optional: rule.optional === undefined ? false : rule.optional
            });
        let hasValuesLeft = false;
        // Filter out values that are set to undefined => deleted (optional properties)
        // eslint-disable-next-line @typescript-eslint/no-for-in-array
        for (const i in values) {
            if (values[i] === undefined) {
                delete values[i];
            }
            else {
                hasValuesLeft = true;
            }
        }
        if (hasValuesLeft) {
            iterateValues(getIterationStrategyFromCB(iterate, params.defaultIterationStrategies.objectValues), itemtype, values, propPath, cb, params, (i, value) => {
                if (value === undefined) {
                    delete objs[i][rule.itemprop];
                }
                else {
                    objs[i][rule.itemprop] = value;
                }
                params.onValueChange(propPath, i, value);
            });
        }
    }
}
function iterateMapValue(recipeValue, maps, path, cb, params, setValue) {
    cb.map &&
        cb.map({
            values: maps,
            valueType: recipeValue,
            path,
            crdtAlgorithm: getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, path, 'map'),
            setValue
        });
    const keys = new Set();
    // Get union of all keys of all maps
    for (const map of maps) {
        if (map !== undefined) {
            for (const [key] of map) {
                keys.add(key);
            }
        }
    }
    for (const key of keys) {
        const values = sparseMap(maps, m => m.get(key));
        const keysForIter = sparseMap(maps, _m => key);
        const keyPath = path.length === 0 ? key : path.concat('.', key);
        const keyPathForIteration = path.length === 0 ? key : path.concat('.!key!', key);
        const iterate = cb.mapEntry &&
            cb.mapEntry({
                values,
                valueType: recipeValue.value,
                path: keyPath,
                crdtAlgorithm: getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, keyPath, 'mapEntry'),
                setValue: (i, value) => {
                    if (value === undefined) {
                        maps[i].delete(key);
                    }
                    else {
                        maps[i].set(key, value);
                    }
                    params.onValueChange(keyPath, i, value);
                },
                key,
                keyType: recipeValue.key
            });
        let hasValuesLeft = false;
        // Filter out values that are set to undefined => deleted (optional properties)
        // eslint-disable-next-line @typescript-eslint/no-for-in-array
        for (const i in values) {
            if (values[i] === undefined) {
                delete values[i];
                delete keysForIter[i];
            }
            else {
                hasValuesLeft = true;
            }
        }
        let keyIterationStrategy;
        let valueIterationStrategy;
        if (isMapIterationStrategy(iterate)) {
            keyIterationStrategy = iterate.keyIterationStrategy;
            valueIterationStrategy = iterate.valueIterationStrategy;
        }
        else if (isIterationStrategy(iterate)) {
            valueIterationStrategy = iterate;
        }
        if (hasValuesLeft) {
            iterateValues(getIterationStrategyFromCB(keyIterationStrategy, params.defaultIterationStrategies.mapKeys), recipeValue.key, keysForIter, keyPathForIteration, cb, params, () => {
                throw new Error('Changing keys is not supported');
            });
            iterateValues(getIterationStrategyFromCB(valueIterationStrategy, params.defaultIterationStrategies.mapValues), recipeValue.value, values, keyPath, cb, params, (i, value) => {
                if (value === undefined) {
                    maps[i].delete(key);
                }
                else {
                    maps[i].set(key, value);
                }
                params.onValueChange(keyPath, i, value);
            });
        }
    }
}
/**
 * Iterate bag value
 *
 * @param {BagValue} recipeValue
 * @param {unknown[]} bags
 * @param {string} path
 * @param {Callbacks} cb
 * @param {Params} params
 * @param {Function} setValue
 */
function iterateBagValue(recipeValue, bags, path, cb, params, setValue) {
    const iterate = cb.bag &&
        cb.bag({
            values: bags,
            valueType: recipeValue,
            path,
            crdtAlgorithm: getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, path, 'bag'),
            setValue
        });
    iterateValueArrays(getIterationStrategyFromCB(iterate, params.defaultIterationStrategies.bagValues), recipeValue.item, bags, path, cb, params);
}
/**
 * Iterate array value
 *
 * @param {ArrayValue} recipeValue
 * @param {unknown[]} arrays
 * @param {string} path
 * @param {Callbacks} cb
 * @param {Params} params
 * @param {Function} setValue
 */
function iterateArrayValue(recipeValue, arrays, path, cb, params, setValue) {
    const iterate = cb.array &&
        cb.array({
            values: arrays,
            valueType: recipeValue,
            path,
            crdtAlgorithm: getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, path, 'array'),
            setValue
        });
    iterateValueArrays(getIterationStrategyFromCB(iterate, params.defaultIterationStrategies.arrayValues), recipeValue.item, arrays, path, cb, params);
}
/**
 * Iterate set value
 *
 * @param {SetValue} recipeValue
 * @param {Set<unknown>} sets
 * @param {string} path
 * @param {Callbacks} cb
 * @param {Params} params
 * @param {Function} setValue
 */
function iterateSetValue(recipeValue, sets, path, cb, params, setValue) {
    const iterate = cb.set &&
        cb.set({
            values: sets,
            valueType: recipeValue,
            path,
            crdtAlgorithm: getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, path, 'set'),
            setValue
        });
    iterateValueArrays(getIterationStrategyFromCB(iterate, params.defaultIterationStrategies.setValues), recipeValue.item, sparseMap(sets, v => [...v]), path, cb, params, (i, elementIndex, value) => {
        // We do this weird thing, because we do not want to create a new set.
        // Otherwise, the set that we passed to the cb.set callback would be a different one.
        [...sets].forEach((v, j) => {
            sets[i].delete(v);
            sets[i].add(j === elementIndex ? value : v);
        });
        params.onValueChange(path, i, value);
    });
}
// ######## Common iterations of the ValueType iterators ########
function iterateValues(iterate, valueType, values, path, cb, params, setValue) {
    if (iterate === 'parallel') {
        iterateRecipeValue(valueType, values, path, cb, params, setValue);
    }
    else if (iterate === 'separate') {
        // eslint-disable-next-line @typescript-eslint/no-for-in-array
        for (const i in values) {
            const newValues = [];
            newValues[i] = values[i];
            iterateRecipeValue(valueType, newValues, path, cb, params, setValue);
        }
    }
}
function iterateValueArrays(iterate, valueType, arrays, path, cb, params, setValue) {
    if (iterate === 'parallel') {
        const lengths = sparseMap(arrays, v => v.length);
        const maxLength = Math.max(...lengths.filter(v => v !== undefined));
        for (let i = 0; i < maxLength; ++i) {
            const valuePath = path + `.${i}`;
            const values = new Array(arrays.length);
            for (const idx in arrays) {
                if (i < arrays[idx].length) {
                    values[idx] = arrays[idx][i];
                }
            }
            iterateRecipeValue(valueType, values, valuePath, cb, params, (index, value) => {
                if (setValue) {
                    setValue(index, i, value);
                }
                else {
                    arrays[index][i] = value;
                }
                params.onValueChange(path, index, value);
            });
        }
    }
    else if (iterate === 'separate') {
        // eslint-disable-next-line @typescript-eslint/no-for-in-array
        for (const sparseIndex in arrays) {
            for (let i = 0; i < arrays[sparseIndex].length; ++i) {
                const valuePath = path + `.${i}`;
                const values = new Array(arrays.length);
                if (sparseIndex in arrays && i < arrays[sparseIndex].length) {
                    values[sparseIndex] = arrays[sparseIndex][i];
                }
                else {
                    continue;
                }
                iterateRecipeValue(valueType, values, valuePath, cb, params, (index, value) => {
                    arrays[index][i] = value;
                    params.onValueChange(path, index, value);
                });
            }
        }
    }
}
function getIterationStrategyFromCB(iterationStrategy, defaultStrategy) {
    if (iterationStrategy === undefined) {
        return defaultStrategy;
    }
    else if (iterationStrategy === 'parallel' || iterationStrategy === 'separate') {
        return iterationStrategy;
    }
    else {
        return 'off';
    }
}
//# sourceMappingURL=iterate-objects-sync.js.map